import React, { useState, useEffect } from 'react';
import Modal from '../common/Modal';
import { TestScenarioSelector } from './components/TestScenarioSelector';
import { TestExecutionControls } from './components/TestExecutionControls';
import { TestProgressIndicator } from './components/TestProgressIndicator';
import { TestResultsPanel } from './components/TestResultsPanel';
import { APIMonitor } from './components/APIMonitor';
import { testExecutionService, TestScenario as ServiceTestScenario, TestResults as ServiceTestResults, APICallLog } from '../../services/testExecutionService';
import { useTestExecutionWebSocket } from '../../hooks/useTestExecutionWebSocket';

// Use service interfaces
export interface TestScenario extends ServiceTestScenario {}

export interface TestExecution {
  id: string;
  status: 'idle' | 'running' | 'completed' | 'failed';
  scenarios: TestScenario[];
  startTime?: Date;
  endTime?: Date;
  results?: TestResults;
  currentScenario?: string;
  completedScenarios?: number;
}

export interface TestResults {
  passed: number;
  failed: number;
  skipped: number;
  duration: number;
  details: TestStepResult[];
}

export interface TestStepResult {
  scenario: string;
  status: 'passed' | 'failed' | 'skipped';
  duration: number;
  error?: string;
  screenshots?: string[];
}

const AVAILABLE_SCENARIOS: TestScenario[] = [
  {
    id: 'auth-flow',
    name: 'Authentication Flow',
    description: 'Tests login, 2FA handling, and authentication redirects',
    command: 'npm run test:e2e src/test/e2e/auth.spec.ts',
    category: 'auth',
    duration: '~2 min'
  },
  {
    id: 'complete-workflow',
    name: 'Complete Job Workflow',
    description: 'Full end-to-end job creation and execution workflow',
    command: 'npm run test:e2e src/test/e2e/autopilot/complete-job-workflow.spec.ts',
    category: 'workflow',
    duration: '~5 min'
  },
  {
    id: 'platform-selection',
    name: 'Platform Selection',
    description: 'Tests platform discovery and selection interface',
    command: 'npm run test:e2e src/test/e2e/autopilot/platform-selection-discovery.spec.ts',
    category: 'platform',
    duration: '~3 min'
  },
  {
    id: 'record-selection',
    name: 'Record Selection Workflow',
    description: 'Tests data extraction and record selection process',
    command: 'npm run test:e2e src/test/e2e/autopilot/record-selection-workflow.spec.ts',
    category: 'workflow',
    duration: '~4 min'
  },
  {
    id: 'performance-audit',
    name: 'Performance Audit',
    description: 'Lighthouse performance testing and Web Vitals analysis',
    command: 'npm run test:e2e src/test/e2e/lighthouse-audit.spec.ts',
    category: 'performance',
    duration: '~3 min'
  },
  {
    id: 'accessibility-audit',
    name: 'Accessibility Audit',
    description: 'AXE accessibility testing and compliance checks',
    command: 'npm run test:e2e src/test/e2e/axe-audit.spec.ts',
    category: 'accessibility',
    duration: '~2 min'
  }
];

interface SecretTestRunnerProps {
  isOpen: boolean;
  onClose: () => void;
}

const SecretTestRunner: React.FC<SecretTestRunnerProps> = ({ isOpen, onClose }) => {
  const [availableScenarios, setAvailableScenarios] = useState<TestScenario[]>([]);
  const [selectedScenarios, setSelectedScenarios] = useState<string[]>([]);
  const [testExecution, setTestExecution] = useState<TestExecution>({
    id: '',
    status: 'idle',
    scenarios: []
  });
  const [apiCalls, setApiCalls] = useState<APICallLog[]>([]);
  const [executionId, setExecutionId] = useState<string | null>(null);
  const [isUsingMockMode, setIsUsingMockMode] = useState(false);

  // WebSocket integration for real-time updates
  const { connected: wsConnected, error: wsError } = useTestExecutionWebSocket({
    executionId,
    onProgressUpdate: (update) => {
      setTestExecution(prev => ({
        ...prev,
        currentScenario: update.currentScenario,
        completedScenarios: update.completedScenarios
      }));
    },
    onApiCall: (apiCall) => {
      setApiCalls(prev => [...prev.slice(-10), apiCall]);
    },
    onComplete: (results) => {
      setTestExecution(prev => ({
        ...prev,
        status: 'completed',
        endTime: new Date(),
        results: convertServiceResultsToUIResults(results)
      }));
      setExecutionId(null);
    },
    onError: (error) => {
      console.error('Test execution error:', error);
      setTestExecution(prev => ({
        ...prev,
        status: 'failed',
        endTime: new Date()
      }));
      setExecutionId(null);
    }
  });

  // Load available scenarios only when modal opens
  useEffect(() => {
    if (!isOpen) return;

    const loadScenarios = async () => {
      try {
        const scenarios = await testExecutionService.getAvailableScenarios();
        setAvailableScenarios(scenarios);
      } catch (error) {
        console.error('Failed to load scenarios, using defaults:', error);
        setAvailableScenarios(AVAILABLE_SCENARIOS);
      }
    };
    
    loadScenarios();
  }, [isOpen]);

  // Convert service results to UI format
  const convertServiceResultsToUIResults = (serviceResults: ServiceTestResults): TestResults => {
    return {
      passed: serviceResults.passed,
      failed: serviceResults.failed,
      skipped: serviceResults.skipped,
      duration: serviceResults.duration,
      details: serviceResults.scenarios.map(scenario => ({
        scenario: scenario.name,
        status: scenario.status,
        duration: scenario.duration,
        error: scenario.error,
        screenshots: scenario.screenshots
      }))
    };
  };

  // Real test execution
  const executeTests = async (scenarioIds: string[]) => {
    const scenarios = availableScenarios.filter(s => scenarioIds.includes(s.id));
    
    try {
      // Try real API execution first
      const response = await testExecutionService.executeTests({
        scenarios: scenarioIds,
        config: {
          baseUrl: window.location.origin,
          headless: true,
          timeout: 30000
        }
      });

      const newExecutionId = response.executionId;
      setExecutionId(newExecutionId);
      setIsUsingMockMode(false);

      setTestExecution({
        id: newExecutionId,
        status: 'running',
        scenarios,
        startTime: new Date(),
        completedScenarios: 0
      });

      // WebSocket will handle real-time updates
      console.log('Test execution started:', newExecutionId);

    } catch (error) {
      console.error('Failed to start real test execution, falling back to mock mode:', error);
      
      // Fallback to mock execution
      setIsUsingMockMode(true);
      const mockExecution = await testExecutionService.mockTestExecution(scenarioIds);
      
      setTestExecution({
        id: mockExecution.executionId,
        status: 'running',
        scenarios,
        startTime: new Date(),
        completedScenarios: 0
      });

      // Simulate progress updates for mock mode
      simulateMockProgress(scenarios, mockExecution.mockResults);
    }
  };

  // Simulate mock progress for fallback mode
  const simulateMockProgress = async (scenarios: TestScenario[], mockResults: ServiceTestResults) => {
    let completedTests = 0;
    const totalTests = scenarios.length;
    
    for (const scenario of scenarios) {
      // Simulate test execution time
      await new Promise(resolve => setTimeout(resolve, 2000 + Math.random() * 3000));
      
      completedTests++;
      
      // Mock API call logging
      setApiCalls(prev => [...prev.slice(-10), {
        timestamp: new Date().toISOString(),
        method: 'POST',
        url: `/api/tests/execute`,
        status: 200,
        scenario: scenario.name,
        duration: Math.random() * 200 + 50
      }]);
      
      // Update progress
      setTestExecution(prev => ({
        ...prev,
        completedScenarios: completedTests,
        currentScenario: completedTests < totalTests ? scenarios[completedTests].name : undefined
      }));
      
      if (completedTests === totalTests) {
        setTestExecution(prev => ({
          ...prev,
          status: 'completed',
          endTime: new Date(),
          results: convertServiceResultsToUIResults(mockResults)
        }));
      }
    }
  };

  const stopTests = async () => {
    if (executionId && !isUsingMockMode) {
      try {
        await testExecutionService.stopExecution(executionId);
      } catch (error) {
        console.error('Failed to stop test execution:', error);
      }
    }
    
    setTestExecution(prev => ({
      ...prev,
      status: 'failed',
      endTime: new Date()
    }));
    setExecutionId(null);
  };

  const resetTests = () => {
    setTestExecution({
      id: '',
      status: 'idle',
      scenarios: []
    });
    setApiCalls([]);
    setExecutionId(null);
    setIsUsingMockMode(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="🧪 Secret Test Runner" size="full">
      <div className="space-y-6">
        <div className="mb-6">
          <p className="text-gray-600 dark:text-gray-400">
            Execute comprehensive Playwright tests directly from the browser interface
          </p>
        </div>

        {/* Main Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Test Controls */}
          <div className="lg:col-span-1 space-y-6">
            <TestScenarioSelector
              scenarios={availableScenarios.length > 0 ? availableScenarios : AVAILABLE_SCENARIOS}
              selectedScenarios={selectedScenarios}
              onSelectionChange={setSelectedScenarios}
              isExecuting={testExecution.status === 'running'}
            />
            
            <TestExecutionControls
              selectedScenarios={selectedScenarios}
              executionStatus={testExecution.status}
              onExecute={() => executeTests(selectedScenarios)}
              onStop={stopTests}
              onReset={resetTests}
            />
            
            <TestProgressIndicator
              execution={testExecution}
            />
          </div>

          {/* Right Column - Results and Monitoring */}
          <div className="lg:col-span-2 space-y-6">
            {/* Connection Status Indicator */}
            {executionId && !isUsingMockMode && (
              <div className={`p-3 rounded-lg border ${
                wsConnected 
                  ? 'border-green-200 bg-green-50 dark:bg-green-900/20 dark:border-green-700' 
                  : 'border-yellow-200 bg-yellow-50 dark:bg-yellow-900/20 dark:border-yellow-700'
              }`}>
                <div className="flex items-center space-x-2 text-sm">
                  <div className={`w-2 h-2 rounded-full ${
                    wsConnected ? 'bg-green-500 animate-pulse' : 'bg-yellow-500'
                  }`} />
                  <span className={wsConnected ? 'text-green-800 dark:text-green-300' : 'text-yellow-800 dark:text-yellow-300'}>
                    {wsConnected ? 'Connected to test runner' : 'Connecting to test runner...'}
                  </span>
                  {wsError && (
                    <span className="text-red-600 dark:text-red-400 text-xs">
                      ({wsError})
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Mock Mode Indicator */}
            {isUsingMockMode && (
              <div className="p-3 rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-700">
                <div className="flex items-center space-x-2 text-sm text-blue-800 dark:text-blue-300">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Running in demo mode - API backend not available</span>
                </div>
              </div>
            )}

            <APIMonitor
              apiCalls={apiCalls}
              isMonitoring={testExecution.status === 'running'}
            />
            
            <TestResultsPanel
              results={testExecution.results}
              execution={testExecution}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default SecretTestRunner;